import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShareNetwork, Plus, Star, PlugsConnected, UsersThree, Power } from "@phosphor-icons/react";

const EMPTY = { name: "", host: "", port: "5060", transport: "UDP", cred_username: "", cred_secret: "", region: "", notes: "" };

export default function OpensipsServers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [custDlg, setCustDlg] = useState(null);
  const [custRows, setCustRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/servers/opensips")).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEdit(s);
    setForm({ name: s.name, host: s.host, port: String(s.port), transport: s.transport || "UDP",
      cred_username: s.cred_username || "", cred_secret: "", region: s.region || "", notes: s.notes || "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, host: form.host, port: parseInt(form.port || "5060"),
        transport: form.transport, cred_username: form.cred_username || null,
        region: form.region || null, notes: form.notes || null };
      if (form.cred_secret) body.cred_secret = form.cred_secret;
      if (edit) await api.patch(`/servers/opensips/${edit.id}`, body);
      else await api.post("/servers/opensips", body);
      toast.success(edit ? "Server updated" : "Server added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (fn, id) => { setBusy(id); try { await fn(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); } };
  const del = (id) => act(async () => { await api.delete(`/servers/opensips/${id}`); toast.success("Server removed"); }, id);
  const toggleStatus = (s) => act(async () => { await api.post(`/servers/opensips/${s.id}/status`, { status: s.status === "active" ? "inactive" : "active" }); toast.success("Status updated"); }, s.id);
  const setDefault = (s) => act(async () => { await api.post(`/servers/opensips/${s.id}/default`, { is_default: !s.is_default }); toast.success(s.is_default ? "Default cleared" : "Set as default"); }, s.id);
  const test = (s) => act(async () => { const { data } = await api.post(`/servers/opensips/${s.id}/test`); toast.success(`Reachable · ${data.latency_ms} ms (simulated)`); }, s.id);
  const viewCustomers = async (s) => {
    setCustDlg(s);
    try { setCustRows((await api.get(`/servers/opensips/${s.id}/customers`)).data); } catch { setCustRows([]); }
  };

  return (
    <div>
      <PageHeader title="OpenSIPS Servers" subtitle="SIP edge servers assignable to customers">
        <Button onClick={openNew} data-testid="create-opensips-btn" className="rounded-lg"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add Server</Button>
      </PageHeader>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="opensips-empty" icon={ShareNetwork} title="No OpenSIPS servers" description="Add SIP edge servers so you can assign them to customers." />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="opensips-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Host</TableHead><TableHead>Port</TableHead>
                <TableHead>Transport</TableHead><TableHead>Default</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id} className="hover:bg-muted/40" data-testid={`opensips-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="font-mono text-sm">{s.host}</TableCell>
                    <TableCell className="tabular-nums">{s.port}</TableCell>
                    <TableCell className="text-sm">{s.transport || "UDP"}</TableCell>
                    <TableCell>
                      <button onClick={() => setDefault(s)} disabled={busy === s.id} data-testid={`opensips-default-${s.id}`}
                        className={`inline-flex items-center gap-1 text-xs ${s.is_default ? "text-amber-500" : "text-muted-foreground hover:text-foreground"}`}>
                        <Star className="w-4 h-4" weight={s.is_default ? "fill" : "regular"} /> {s.is_default ? "Default" : "Set"}
                      </button>
                    </TableCell>
                    <TableCell><StatusPill status={s.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => test(s)} disabled={busy === s.id} data-testid={`opensips-test-${s.id}`} title="Test connectivity"><PlugsConnected className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => viewCustomers(s)} data-testid={`opensips-customers-${s.id}`} title="Assigned customers"><UsersThree className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => toggleStatus(s)} disabled={busy === s.id} data-testid={`opensips-toggle-${s.id}`} title={s.status === "active" ? "Deactivate" : "Activate"}><Power className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEdit(s)} data-testid={`edit-opensips-${s.id}`}>Edit</Button>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => del(s.id)} data-testid={`delete-opensips-${s.id}`}>Delete</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="opensips-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit" : "Add"} OpenSIPS server</DialogTitle>
            <DialogDescription>Platform SIP edge server (assignable to customers).</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name</Label><Input data-testid="opensips-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2 col-span-2"><Label>Host / IP</Label><Input data-testid="opensips-host" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="10.0.0.10" /></div>
              <div className="space-y-2"><Label>Port</Label><Input data-testid="opensips-port" type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Transport</Label>
                <Select value={form.transport} onValueChange={(v) => setForm({ ...form, transport: v })}>
                  <SelectTrigger data-testid="opensips-transport"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="UDP">UDP</SelectItem><SelectItem value="TCP">TCP</SelectItem><SelectItem value="TLS">TLS</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Region</Label><Input data-testid="opensips-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="us-east-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Credential username <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="opensips-cred-user" value={form.cred_username} onChange={(e) => setForm({ ...form, cred_username: e.target.value })} /></div>
              <div className="space-y-2"><Label>Credential secret <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="opensips-cred-secret" type="password" value={form.cred_secret} onChange={(e) => setForm({ ...form, cred_secret: e.target.value })} placeholder={edit?.has_cred_secret ? "•••••• (leave blank to keep)" : ""} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} data-testid="submit-opensips-btn" className="rounded-lg">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!custDlg} onOpenChange={(v) => !v && setCustDlg(null)}>
        <DialogContent data-testid="opensips-customers-dialog">
          <DialogHeader><DialogTitle>Customers using {custDlg?.name}</DialogTitle>
            <DialogDescription>Customers this OpenSIPS server is currently assigned to.</DialogDescription></DialogHeader>
          <div className="py-2 space-y-2 max-h-[50vh] overflow-y-auto">
            {custRows.length === 0 && <p className="text-sm text-muted-foreground">No customers assigned.</p>}
            {custRows.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`opensips-cust-${c.id}`}>
                <div><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-muted-foreground">{c.company || "—"}</p></div>
                <StatusPill status={c.status} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
